// Shared search state + navigation logic for the top-level Mixpedia search
// (formerly inlined in NavbarSearch). Both the desktop inline-dropdown
// (NavbarSearch) and the mobile full-screen overlay (MobileSearchOverlay)
// consume this so the autocomplete/recent-history/navigation behavior is
// identical across surfaces.

import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { setActiveTab } from '../redux/slices/appSlice';
import { openUnifiedResults, openMixpediaLabel } from '../redux/thunk/appNavThunk';
import {
    setActiveSearch,
    setArtistQuery,
    setTrackQuery,
    setMixQuery,
    setSearchAllQuery,
    setMixFilterContext,
} from '../redux/slices/djpalSlice';
import {
    searchArtist,
    searchTrack,
    searchMixes,
    ensureToken,
    runTrackTabSearch,
} from '../redux/thunk/djpalThunk';
import {
    fetchSearchHistory,
    addSearchHistoryEntry,
    deleteSearchHistoryEntry,
    clearSearchHistoryBySurface,
} from '../redux/thunk/searchHistoryThunk';
import { selectSurfaceLoaded } from '../redux/slices/searchHistorySlice';
import { buildMixOpenFilters } from '../utils/mixNavigation';
import { MIN_SEARCH_CHARS } from '../utils/searchConfig';
import { orderSections, normalizeEventKind } from '../utils/searchSections';
import { eventFilters, eventFilterContext, eventHistoryEntry } from '../utils/eventNav';
import { entityId } from '../utils/entityId';
import { track as trackEvent } from '../utils/analytics';

// Flatten grouped suggestions into a single indexable list for keyboard nav:
// "See all" → the four catalog sections in `order` (computed once per fetch
// by orderSections — best-match score per section, see utils/searchSections).
const buildFlatList = ({ artists, tracks, mixes, events = [], labels = [] }, query, order) => {
    const list = [];
    if (query && query.trim().length >= MIN_SEARCH_CHARS) list.push({ type: 'all', query: query.trim() });
    const push = {
        events: () => events.forEach((ev) => list.push({ type: 'event', item: ev })),
        artists: () => artists.forEach((a) => list.push({ type: 'artist', item: a })),
        tracks: () => tracks.forEach((t) => list.push({ type: 'track', item: t })),
        mixes: () => mixes.forEach((m) => list.push({ type: 'mix', item: m })),
        labels: () => labels.forEach((l) => list.push({ type: 'label', item: l })),
    };
    order.forEach((k) => push[k] && push[k]());
    return list;
};

const EMPTY_GROUPS = { artists: [], tracks: [], mixes: [], events: [], labels: [] };

export const useUnifiedSearch = () => {
    const [query, setQueryState] = useState('');
    const [groups, setGroups] = useState(EMPTY_GROUPS);
    // Catalog-section display order, computed alongside `flat` at fetch time
    // so the rendered sections and the flat keyboard-nav list always agree.
    const [sectionOrder, setSectionOrder] = useState(['artists', 'events', 'tracks', 'mixes', 'labels']);
    const [flat, setFlat] = useState([]);
    const [loading, setLoading] = useState(false);

    const debounceRef = useRef(null);
    const fetchSeqRef = useRef(0);

    const dispatch = useDispatch();
    const store = useStore();
    const allHistoryItems = useSelector((state) => state.searchHistory.items);
    const historyItems = useMemo(
        () => allHistoryItems.filter((e) => (e.surface || 'mixpedia') === 'mixpedia'),
        [allHistoryItems],
    );
    const historyLoaded = useSelector(selectSurfaceLoaded('mixpedia'));

    useEffect(() => {
        if (!historyLoaded) dispatch(fetchSearchHistory({ surface: 'mixpedia', limit: 10 }));
    }, [dispatch, historyLoaded]);

    const fetchSuggestions = useCallback(async (q) => {
        if (q.length < MIN_SEARCH_CHARS) {
            setGroups(EMPTY_GROUPS);
            setFlat([]);
            return;
        }
        const seq = ++fetchSeqRef.current;
        setLoading(true);
        try {
            const token = await ensureToken(dispatch, store.getState);
            const result = await window.electron.djpal.autocompleteUnified(token, q);
            if (seq !== fetchSeqRef.current) return;
            if (result.error) {
                setGroups(EMPTY_GROUPS);
                setFlat([]);
                return;
            }
            const next = {
                artists: result.artists || [],
                tracks: result.tracks || [],
                mixes: result.mixes || [],
                events: result.events || [],
                // Servers that predate label search send no `labels`.
                labels: result.labels || [],
            };
            const nextOrder = orderSections(next, q);
            setGroups(next);
            setSectionOrder(nextOrder);
            setFlat(buildFlatList(next, q, nextOrder));
        } catch {
            if (seq === fetchSeqRef.current) {
                setGroups(EMPTY_GROUPS);
                setFlat([]);
            }
        } finally {
            if (seq === fetchSeqRef.current) setLoading(false);
        }
    }, [dispatch, store]);

    const setQuery = useCallback((val) => {
        setQueryState(val);
        clearTimeout(debounceRef.current);
        if (val.trim().length < MIN_SEARCH_CHARS) {
            // Bump the seq so any slow in-flight fetch from a longer prior
            // query (e.g. user typed "paul" then deleted it) is invalidated:
            // it can no longer set stale groups OR clear/keep the spinner.
            // And clear the spinner NOW — otherwise a still-pending fetch
            // leaves it spinning over an empty input until it resolves.
            fetchSeqRef.current++;
            setGroups(EMPTY_GROUPS);
            setFlat([]);
            setLoading(false);
            return;
        }
        debounceRef.current = setTimeout(() => fetchSuggestions(val.trim()), 300);
    }, [fetchSuggestions]);

    // Reset everything — used by navigate-and-close callers.
    const reset = useCallback(() => {
        clearTimeout(debounceRef.current);
        fetchSeqRef.current++;
        setQueryState('');
        setGroups(EMPTY_GROUPS);
        setFlat([]);
        setLoading(false);
    }, []);

    const navigateToArtist = useCallback((artist) => {
        const name = typeof artist === 'string' ? artist : artist.name;
        const artistId = typeof artist === 'object'
            ? entityId(artist)
            : null;
        try { window.electron.analytics.track('navbar_search_select', { type: 'artist', name }); } catch (_) { /* analytics best-effort */ }
        trackEvent('search_result_clicked', { result_type: 'artist', name });
        // All navigate* handlers: sub-tab + query BEFORE setActiveTab('djpal')
        // so the nav-history marker pushed on the tab switch is typed and the
        // search result REPLACES it — one Back returns to the originating page,
        // not Mixpedia home (same trap as RowActions.searchInMixpedia).
        dispatch(setActiveSearch('artist'));
        dispatch(setArtistQuery(name));
        dispatch(setActiveTab('djpal'));
        dispatch(searchArtist({ artistName: name, artistId: artistId || null, artworkUrl: (typeof artist === 'object' && artist.artworkUrl) || null }));
        dispatch(addSearchHistoryEntry({
            entityType: 'artist',
            entityPublicId: artistId || `name:${name}`, // searchHistory row field; slug or name: placeholder
            entityName: name,
            entityArtwork: (typeof artist === 'object' && artist.artworkUrl) || null,
            surface: 'mixpedia',
        }));
        reset();
    }, [dispatch, reset]);

    const navigateToTrack = useCallback((track) => {
        const q = track.artist ? `${track.artist} - ${track.title}` : track.title;
        try { window.electron.analytics.track('navbar_search_select', { type: 'track', name: q }); } catch (_) { /* analytics best-effort */ }
        trackEvent('search_result_clicked', { result_type: 'track', name: q });
        dispatch(setActiveSearch('track'));
        dispatch(setTrackQuery(q));
        dispatch(setActiveTab('djpal'));
        dispatch(searchTrack({ trackName: q, trackId: entityId(track), artworkUrl: track.artworkUrl || null }));
        dispatch(addSearchHistoryEntry({
            entityType: 'track',
            entityPublicId: entityId(track) || `q:${q}`,
            entityName: track.title,
            entitySubtitle: track.artist || null,
            entityArtwork: track.artworkUrl || null,
            surface: 'mixpedia',
        }));
        reset();
    }, [dispatch, reset]);

    const navigateToMix = useCallback((mix) => {
        const q = mix.djName ? `${mix.djName} - ${mix.title}` : mix.title;
        try { window.electron.analytics.track('navbar_search_select', { type: 'mix', name: q }); } catch (_) { /* analytics best-effort */ }
        trackEvent('search_result_clicked', { result_type: 'mix', name: q });
        // Id-only navigation via the shared helper (single source of truth
        // with NavbarSearch — keep them identical). The old comment here
        // claimed a slug under `id` falls through to a title filter — WRONG:
        // the db layer maps string `id` → m.public_id, so one key covers
        // both shapes. No title fallback: autocomplete mixes always carry an
        // id or a link, and a title search would open a namesake.
        const filters = buildMixOpenFilters(mix);
        if (!filters) {
            console.debug('[mix open] no id/link on mix, ignoring click', mix?.title);
            return;
        }
        dispatch(setActiveSearch('mix'));
        dispatch(setMixQuery(q));
        dispatch(setActiveTab('djpal'));
        dispatch(searchMixes({ filters }));
        // Same id derivation as NavbarSearch (keep the twins identical):
        // entityId() prefers the API-normalized `.id` (the public slug).
        const historyPublicId = entityId(mix);
        dispatch(addSearchHistoryEntry({
            entityType: 'mix',
            entityPublicId: historyPublicId || `q:${q}`,
            entityName: mix.title,
            entitySubtitle: mix.djName || null,
            entityArtwork: mix.artworkUrl || null,
            surface: 'mixpedia',
        }));
        reset();
    }, [dispatch, reset]);

    // Event/venue suggestion ("Tomorrowland · 330 mixes") — the destination
    // is the title-filtered PAGINATED mixes list, chip'd with the event name.
    const navigateToEvent = useCallback((ev) => {
        const name = ev.name || '';
        if (!name) return;
        trackEvent('search_result_clicked', { result_type: 'event', name, kind: normalizeEventKind(ev.kind) });
        dispatch(setActiveSearch('mix'));
        dispatch(setMixQuery(name));
        dispatch(setActiveTab('djpal'));
        dispatch(setMixFilterContext(eventFilterContext(ev)));
        dispatch(searchMixes({ filters: eventFilters(ev), paginate: true, lite: true }));
        dispatch(addSearchHistoryEntry(eventHistoryEntry(ev)));
        reset();
    }, [dispatch, reset]);

    // Label suggestion → the label page (openMixpediaLabel owns the nav
    // entry push and the tab switch).
    const navigateToLabel = useCallback((label) => {
        const publicId = label?.publicId || label?.id;
        if (!publicId) return;
        trackEvent('search_result_clicked', { result_type: 'label', name: label.name });
        dispatch(openMixpediaLabel({ publicId, name: label.name, from: 'search' }));
        dispatch(addSearchHistoryEntry({
            entityType: 'label',
            entityPublicId: publicId,
            entityName: label.name,
            entitySubtitle: label.trackCount ? `${label.trackCount} tracks` : 'Label',
            entityArtwork: label.artworkUrl || null,
            surface: 'mixpedia',
        }));
        reset();
    }, [dispatch, reset]);

    const goToResultsPage = useCallback((q) => {
        try { window.electron.analytics.track('navbar_search', { query: q }); } catch (_) { /* analytics best-effort */ }
        trackEvent('search_performed', { search_type: 'unified', query: q });
        // openUnifiedResults owns the nav-entry push — a search submitted
        // while already ON Mixpedia gets no setActiveTab push (dedupe), so
        // without it the results page never entered the back stack.
        dispatch(openUnifiedResults(q));
        dispatch(addSearchHistoryEntry({ entityType: 'query', queryText: q, surface: 'mixpedia' }));
        reset();
    }, [dispatch, reset]);

    const navigate = useCallback((entry) => {
        if (entry.type === 'all') goToResultsPage(entry.query);
        else if (entry.type === 'event') navigateToEvent(entry.item);
        else if (entry.type === 'artist') navigateToArtist(entry.item);
        else if (entry.type === 'track') navigateToTrack(entry.item);
        else if (entry.type === 'mix') navigateToMix(entry.item);
        else if (entry.type === 'label') navigateToLabel(entry.item);
    }, [goToResultsPage, navigateToEvent, navigateToArtist, navigateToTrack, navigateToMix, navigateToLabel]);

    const reRunHistory = useCallback((entry) => {
        if (entry.entityType === 'artist') {
            navigateToArtist({
                name: entry.entityName,
                id: entry.entityPublicId && !entry.entityPublicId.startsWith('name:') ? entry.entityPublicId : undefined,
                artworkUrl: entry.entityArtwork || undefined,
            });
        } else if (entry.entityType === 'track') {
            const title = entry.entityName || '';
            const artist = entry.entitySubtitle || '';
            const isCanonical = entry.entityPublicId && !entry.entityPublicId.startsWith('q:');
            if (isCanonical) {
                navigateToTrack({
                    title,
                    artist,
                    id: entry.entityPublicId,
                    artworkUrl: entry.entityArtwork || undefined,
                });
            } else {
                // Placeholder entry — let runTrackTabSearch route to a list view
                // when the query doesn't resolve to a single track.
                const q = artist ? `${artist} - ${title}` : title;
                if (!q) return;
                try { window.electron.analytics.track('navbar_search_select', { type: 'track', name: q }); } catch (_) { /* analytics best-effort */ }
                trackEvent('search_result_clicked', { result_type: 'track', name: q });
                // runTrackTabSearch's synchronous prefix (before its first
                // await) sets the query and routes activeSearch to
                // 'track'/'trackList' — dispatch it BEFORE setActiveTab so the
                // nav marker is typed and the result replaces it (one Back).
                dispatch(runTrackTabSearch({ query: q, artworkUrl: entry.entityArtwork || null }));
                dispatch(setActiveTab('djpal'));
                dispatch(addSearchHistoryEntry({
                    entityType: 'track',
                    entityPublicId: `q:${q}`,
                    entityName: title,
                    entitySubtitle: artist || null,
                    entityArtwork: entry.entityArtwork || null,
                    surface: 'mixpedia',
                }));
                reset();
            }
        } else if (entry.entityType === 'mix') {
            navigateToMix({
                title: entry.entityName || '',
                djName: entry.entitySubtitle || '',
                publicId: entry.entityPublicId && !entry.entityPublicId.startsWith('q:') ? entry.entityPublicId : undefined,
                artworkUrl: entry.entityArtwork || undefined,
            });
        } else if (entry.entityType === 'event') {
            navigateToEvent({ name: entry.entityName || '', covers: entry.entityArtwork ? [entry.entityArtwork] : [] });
        } else if (entry.entityType === 'label') {
            navigateToLabel({ publicId: entry.entityPublicId, name: entry.entityName || '', artworkUrl: entry.entityArtwork || null });
        } else {
            goToResultsPage(entry.queryText || '');
        }
    }, [dispatch, reset, navigateToArtist, navigateToTrack, navigateToMix, navigateToEvent, navigateToLabel, goToResultsPage]);

    const dismissHistoryEntry = useCallback((id) => {
        dispatch(deleteSearchHistoryEntry(id));
    }, [dispatch]);

    const clearAllHistory = useCallback(() => {
        // Only wipes the Mixpedia surface — Set Planner recents are owned by
        // that tab and shouldn't be cleared from here.
        dispatch(clearSearchHistoryBySurface('mixpedia'));
    }, [dispatch]);

    const submitFreeText = useCallback(() => {
        const q = query.trim();
        if (q.length >= 2) goToResultsPage(q);
    }, [query, goToResultsPage]);

    return {
        query,
        setQuery,
        groups,
        sectionOrder,
        flat,
        loading,
        historyItems,
        reset,
        navigate,
        navigateToArtist,
        navigateToTrack,
        navigateToMix,
        navigateToEvent,
        navigateToLabel,
        goToResultsPage,
        reRunHistory,
        dismissHistoryEntry,
        clearAllHistory,
        submitFreeText,
    };
};
